// FILE: /src/Components/Onboarding.js (FULLY UPDATED & CORRECTED)

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Spin, message } from 'antd';
import Organization from './Organization';
import AssetsData from './AssetsData';
import { getProfileDetails, checkOnboardingStatus } from '../services/group';
import { handleOnboarding } from '../services/user.profile';
import { LoadingOutlined, ExclamationCircleOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setAns1, setAns2, setAns3 } from '../store/Slices/OnboardingSlice';
import { fetchGroups } from '../store/Slices/GroupSlice';

const OptionCard = ({ label, isSelected, onClick }) => (
<button
onClick={onClick}
className={`relative group flex items-center justify-center p-4 h-16 w-full text-sm font-semibold rounded-xl border transition-all duration-300 ease-out ${isSelected ? 'border-brand-primary bg-purple-50 text-brand-primary shadow-md ring-1 ring-purple-200' : 'border-gray-200 bg-white text-gray-600 hover:border-purple-300 hover:shadow-sm hover:-translate-y-0.5'}`}
>
{label}
{isSelected && (<div className="absolute top-1/2 right-3 transform -translate-y-1/2 text-purple-600"><CheckCircleFilled /></div>)}
</button>
);

// --- START: THIS IS THE FIX ---
// We now export this component so it can be used elsewhere.
export const AiProcessingScreen = ({ statusText }) => (
// --- END: THIS IS THE FIX ---
<div className="min-h-screen flex flex-col items-center justify-center bg-white">
<div className="relative w-24 h-24 mb-8">
<div className="absolute inset-0 bg-purple-100 rounded-full animate-ping opacity-75"></div>
<div className="relative bg-white p-6 rounded-full shadow-xl border border-purple-50 flex items-center justify-center h-full w-full">
<LoadingOutlined style={{ fontSize: 40, color: '#7C3AED' }} spin />
</div>
</div>
<h2 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">Analyzing Business DNA</h2>
<div className="h-6 mb-8 flex items-center justify-center">
<span className="text-sm font-medium text-purple-600 animate-pulse bg-purple-50 px-3 py-1 rounded-full">{statusText}</span>
</div>
<div className="w-64 bg-gray-100 rounded-full h-1.5 overflow-hidden">
<div className="bg-purple-600 h-1.5 rounded-full animate-progress origin-left w-full"></div>
</div>
<p className="text-xs text-gray-400 mt-6 max-w-xs text-center">
We are scraping your website and generating assets. <br/>Please do not refresh the page.
</p>
</div>
);

export default function Onboarding() {
const navigate = useNavigate();
const dispatch = useDispatch();
const assetsRef = useRef();

const { groups } = useSelector((state) => state.groups);

const [answers, setAnswers] = useState({ role: '', industry: '', goal: '' });
const [finalOrgData, setFinalOrgData] = useState({ Brandname: "", Brandwebsite: "" });
const [currentStep, setCurrentStep] = useState(1);
const [processingMessage, setProcessingMessage] = useState("Initializing AI Agents...");
const [isSaving, setIsSaving] = useState(false);
const [showManualInputBanner, setShowManualInputBanner] = useState(false);

const roles = ['Content Creator', 'Marketing Agency', 'Small Business', 'Social Media Mgr.'];
const industries = ['Health & Fitness', 'Technology', 'Beauty & Skin', 'Finance', 'Education', 'Food & Beverage', 'Retail / E-comm', 'Real Estate'];
const goals = ['Brand Awareness', 'Lead Generation', 'Grow Followers', 'Sales / Revenue'];

const mapDbToUiStructure = (dbData) => {
if (!dbData) return {};
return {
...dbData,
Brandname: dbData.brand_name || "", Brandwebsite: dbData.website || "",
Brandaddress: dbData.brand_address || "", Brandsize: dbData.brand_size || "",
Brandcountry: dbData.country || "", Brandtimezone: dbData.time_zone || "",
BusinessOverview: dbData.describes || "", mobile: dbData.brand_contact || "",
groupId: dbData.groupId, palette: dbData.palette || "",
website_image_url: dbData.website_image_url || []
};
};

const handleGenerateDNA = () => {
if (!answers.role || !answers.industry || !answers.goal) {
message.warning("Please answer all 3 questions to proceed.");
return;
}
dispatch(setAns1(answers.role));
dispatch(setAns2(answers.industry));
dispatch(setAns3(answers.goal));
setCurrentStep(2);
};

const handleOrgDataChange = (newDataFunc) => {
setFinalOrgData(prev => {
const newData = typeof newDataFunc === 'function' ? newDataFunc(prev) : newDataFunc;
return { ...prev, ...newData };
});
};

const handleFinalSave = async () => {
setIsSaving(true);
try {
const assetsData = assetsRef.current?.getFinalAssetsData() || {};

const payload = {
...finalOrgData,
...assetsData,
brand_name: finalOrgData.Brandname,
website: finalOrgData.Brandwebsite,
describes: finalOrgData.BusinessOverview,
groupId: finalOrgData.groupId,
role: answers.role,
industry: answers.industry,
top_goal: answers.goal,
country: finalOrgData.Brandcountry,
time_zone: finalOrgData.Brandtimezone,
};

await handleOnboarding(payload);
message.success("Setup Complete! Welcome to Oolook.");
navigate("/select-plan");
} catch (error) {
message.error("Failed to save. Please try again.");
} finally {
setIsSaving(false);
}
};


const groupId = useMemo(() => groups?.[0]?._id, [groups]);

useEffect(() => {
if (currentStep !== 2 || !groupId) {
return;
}

let pollingInterval;
let messageInterval;
let isMounted = true;

const handleJobCompletion = async (status) => {
if (!isMounted) return;

message.loading({ content: 'Analysis complete! Loading your brand kit...', duration: 1.5 });
await dispatch(fetchGroups());
const profileDetails = await getProfileDetails(groupId);
setFinalOrgData(mapDbToUiStructure(profileDetails.OnBoarding?.[0] || {}));

if (status === 'failed') {
message.warning("AI analysis encountered an issue. Please complete your brand kit manually.", 6);
setShowManualInputBanner(true);
}
setCurrentStep(3);
};

const pollStatus = async () => {
try {
const result = await checkOnboardingStatus(groupId);
console.log('[Polling] Current status:', result.status);

if (!isMounted) {
clearInterval(pollingInterval);
return;
}

if (result.status === 'completed' || result.status === 'failed') {
clearInterval(pollingInterval);
clearInterval(messageInterval);
handleJobCompletion(result.status);
}
} catch (error) {
console.error("Polling error:", error);
if (isMounted) {
message.error("Could not check brand status. Polling stopped.");
}
clearInterval(pollingInterval);
clearInterval(messageInterval);
}
};

const initializeStep2 = () => {
const messages = ["Scanning website structure...", "Extracting visual assets...", "Analyzing brand colors...", "Generating mission statement...", "Finalizing Business DNA..."];
let msgIndex = 0;
messageInterval = setInterval(() => {
if (isMounted) {
setProcessingMessage(messages[msgIndex % messages.length]);
msgIndex++;
}
}, 4000);

pollStatus();
pollingInterval = setInterval(pollStatus, 5000);
};

initializeStep2();

return () => {
isMounted = false;
clearInterval(pollingInterval);
clearInterval(messageInterval);
};
}, [currentStep, groupId, dispatch]);


return (
<div className="min-h-screen bg-[#F8FAFC] font-sans text-gray-800">
{currentStep === 1 && (
<div className="flex flex-col items-center justify-center min-h-screen p-6">
<div className="w-full max-w-4xl">
<div className="text-center mb-10">
<h2 className="text-4xl font-extrabold text-gray-900 mb-3 tracking-tight">Let's Get Started</h2>
<p className="text-lg text-gray-500">Help us tailor the AI to your specific needs.</p>
</div>
<div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-12 space-y-10">
<div className="space-y-4">
<h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest pl-1">1. Which describes you best?</h3>
<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
{roles.map(r => <OptionCard key={r} label={r} isSelected={answers.role === r} onClick={() => setAnswers(p => ({...p, role: r}))} />)}
</div>
</div>
<div className="space-y-4">
<h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest pl-1">2. What is your industry?</h3>
<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
{industries.map(i => <OptionCard key={i} label={i} isSelected={answers.industry === i} onClick={() => setAnswers(p => ({...p, industry: i}))} />)}
</div>
</div>
<div className="space-y-4">
<h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest pl-1">3. What is your primary goal?</h3>
<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
{goals.map(g => <OptionCard key={g} label={g} isSelected={answers.goal === g} onClick={() => setAnswers(p => ({...p, goal: g}))} />)}
</div>
</div>
<div className="pt-6 flex justify-center">
<button
onClick={handleGenerateDNA}
disabled={!answers.role || !answers.industry || !answers.goal}
className="bg-brand-primary text-white text-lg font-bold py-4 px-16 rounded-full shadow-lg shadow-purple-200 hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:hover:scale-100 disabled:shadow-none flex items-center gap-3"
>
Generate Business DNA <i className="fa-solid fa-wand-magic-sparkles"></i>
</button>
</div>
</div>
</div>
</div>
)}
{currentStep === 2 && <AiProcessingScreen statusText={processingMessage} />}
{currentStep === 3 && (
<div className="max-w-[1600px] mx-auto pb-12 pt-6 px-6">
<div className="flex flex-col md:flex-row justify-between items-center mb-8 bg-white p-4 rounded-xl shadow-sm border border-gray-200 sticky top-4 z-50">
<div>
<h1 className="text-2xl font-extrabold text-gray-900">Your Business DNA</h1>
<p className="text-sm text-gray-500">Review and refine your brand identity.</p>
</div>
<button
onClick={handleFinalSave}
className="mt-4 md:mt-0 bg-green-600 text-white font-bold py-2.5 px-8 rounded-lg shadow-md hover:bg-green-700 hover:shadow-lg transition flex items-center gap-2"
>
{isSaving ? <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: '#fff' }} spin />} /> : <>Save & Finish <i className="fa-solid fa-check"></i></>}
</button>
</div>
{showManualInputBanner && (
<div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-8 rounded-r-md flex items-start animate-fade-in-up">
<ExclamationCircleOutlined className="text-amber-500 text-xl mt-0.5 mr-3" />
<div>
<p className="text-sm text-amber-800 font-bold">Manual Input Required</p>
<p className="text-sm text-amber-700 mt-1">We couldn't automatically scrape all details. Please review and fill in missing info.</p>
</div>
</div>
)}
<div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
<div className="w-full">
<Organization
dataOrganization={finalOrgData}
onDataChange={handleOrgDataChange}
/>
</div>
<AssetsData
ref={assetsRef}
dataOrganization={finalOrgData}
onDataChange={handleOrgDataChange}
groupId={finalOrgData.groupId}
/>
</div>
</div>
)}
</div>
);
}